import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

import AdminAdvertisersTable from "./AdminAdvertisersTable";
import AdminRolesPanel from "./AdminRolesPanel";
import KPICard from "../../components/redesign/elements/KPICard";
import Loader from "../../components/elements/Loader";
import api from "../../api";
import { AdminAdvertiserListItem } from "../../types/advertiserMetricsTypes";
import { ReduxApplicationState } from "../../types/ReduxTypes/ReduxApplicationState";
import { useSetPageMeta } from "../../layouts/AppShellLayout/PageMetaContext";

const MetricsRow = styled("div")({
  display: "flex",
  gap: 16,
  flexWrap: "wrap",
  marginBottom: 24,
});

const Toolbar = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  marginBottom: 12,
  flexWrap: "wrap",
});

const SearchInput = styled("input")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 8,
  fontSize: 13,
  color: theme.palette.brand.gray900,
  padding: "8px 12px",
  outline: "none",
  boxSizing: "border-box" as const,
  width: 280,
  fontFamily: "inherit",
  "&:focus": { borderColor: theme.palette.brand.blueAccent },
  "&::placeholder": { color: theme.palette.brand.gray500 },
}));

const ToggleLabel = styled("label")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.brand.gray500,
  cursor: "pointer",
  userSelect: "none",
}));

const AdministrationPage: FC = () => {
  useSetPageMeta("Administration", "All advertisers and campaigns across the platform");

  const history = useHistory();
  const canManageRoles = useSelector((state: ReduxApplicationState) => state.user.canManageRoles);
  const [advertisers, setAdvertisers] = useState<AdminAdvertiserListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState("");

  const loadAdvertisers = useCallback(async (includeArchived: boolean) => {
    try {
      setIsLoading(true);
      const response = await api.fetchAllAdvertisersForAdmin(includeArchived);
      if (response.success && Array.isArray(response.payload)) {
        setAdvertisers(response.payload);
      } else {
        setAdvertisers([]);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("fetchAllAdvertisersForAdmin error:", error);
      setAdvertisers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdvertisers(showArchived);
  }, [loadAdvertisers, showArchived]);

  const filteredAdvertisers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return advertisers;

    return advertisers.filter((advertiser) => {
      const haystack = [advertiser.name, advertiser.agencyName, advertiser.domain].join(" ").toLowerCase();
      return haystack.includes(query);
    });
  }, [advertisers, search]);

  const totalCampaigns = useMemo(
    () => advertisers.reduce((sum, advertiser) => sum + (advertiser.campaignCount || 0), 0),
    [advertisers],
  );

  const handleRowClick = useCallback(
    (advertiserId: string) => {
      history.push(`/administration/${advertiserId}`);
    },
    [history],
  );

  return (
    <>
      {canManageRoles ? <AdminRolesPanel /> : null}
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <MetricsRow>
            <KPICard label="Advertisers" value={advertisers.length} />
            <KPICard label="Active Campaigns" value={totalCampaigns} />
          </MetricsRow>
          <Toolbar>
            <SearchInput
              type="text"
              placeholder="Search advertisers, agencies, domains…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <ToggleLabel>
              <input type="checkbox" checked={showArchived} onChange={() => setShowArchived((current) => !current)} />
              Show archived
            </ToggleLabel>
          </Toolbar>
          <AdminAdvertisersTable rows={filteredAdvertisers} onRowClick={handleRowClick} />
        </>
      )}
    </>
  );
};

export default AdministrationPage;
